import React, { useCallback, useState } from 'react';

import { Autocomplete, TextField, Typography, useTheme } from '@mui/material';
import type { GridFilterInputValueProps } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { AlineAvatar, AlineCheckbox, FilterTags } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { useGetOrgMembers } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import {
  createAutocompleteFilterOptions,
  filterAutocompleteStyle,
  usePreserveAutocompleteSearchInput,
  useUniqueFilterOptions,
} from './autocomplete-filter-utils';

const useStyles = makeStyles()(theme => ({
  autocomplete: filterAutocompleteStyle,
  avatarText: {
    marginLeft: theme.spacing(1),
  },
  ownerItem: {
    alignItems: 'center',
    display: 'flex',
  },
}));

// Use Partial b/c we have no guarantee of the object shape coming out of apiRef.getRowModels.
export type OwnerFilterOption = Partial<
  Pick<Apollo.User, 'avatarColor' | 'displayName' | 'id' | '__typename'>
>;

const filterOptions = createAutocompleteFilterOptions<OwnerFilterOption>('displayName');

export function OwnerAutocompleteFilter({
  applyValue,
  item,
}: GridFilterInputValueProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const { orgMembers } = useGetOrgMembers({ variables: { filter: Apollo.OrgMemberFilter.Active } });
  const [truncateTags, setTruncateTags] = useState(true);
  const [value, setValue] = useState<OwnerFilterOption[]>(item.value ?? []);
  const compareFn = useCallback(
    (a: OwnerFilterOption, b: OwnerFilterOption) =>
      a.displayName?.localeCompare(b.displayName ?? '') ?? 0,
    []
  );
  const sortedOptions = useUniqueFilterOptions({ compareFn, items: orgMembers });
  const { inputValue, onInputChange } = usePreserveAutocompleteSearchInput();

  function handleValueChange(value: OwnerFilterOption[]): void {
    applyValue({
      ...item,
      value,
    });
    setValue(value);
  }

  return (
    <Autocomplete<OwnerFilterOption, true, false, false>
      disableCloseOnSelect
      fullWidth
      multiple
      openOnFocus
      blurOnSelect={false}
      className={classes.autocomplete}
      filterOptions={filterOptions}
      getOptionKey={option => option.id ?? ''}
      getOptionLabel={option => option.displayName ?? ''}
      inputValue={inputValue}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      options={sortedOptions}
      renderInput={params => {
        return <TextField placeholder="Search owners..." {...params} variant="standard" />;
      }}
      renderOption={(props, option, state) => {
        const { className, key, ...otherProps } = props;
        return (
          <li key={key} {...otherProps} className={clsx(className, classes.ownerItem)}>
            <AlineCheckbox checked={state.selected} style={{ marginRight: theme.spacing(1) }} />
            <AlineAvatar
              avatarInfo={option.displayName?.charAt(0) ?? ''}
              styles={{ backgroundColor: option.avatarColor ?? theme.palette.primary.main }}
            />
            <Typography noWrap className={classes.avatarText}>
              {option.displayName}
            </Typography>
          </li>
        );
      }}
      renderValue={tags => {
        return (
          <FilterTags<OwnerFilterOption>
            getTextFromTag={tag => tag.displayName ?? ''}
            tags={tags}
            truncateTags={truncateTags}
            onDelete={tag => handleValueChange(tags.filter(({ id }) => tag.id !== id))}
          />
        );
      }}
      slotProps={{
        popper: {
          modifiers: [
            {
              enabled: false,
              name: 'flip',
            },
            {
              enabled: false,
              name: 'preventOverflow',
            },
          ],
        },
      }}
      value={value}
      onBlur={e => {
        e.stopPropagation();
        setTruncateTags(true);
      }}
      onChange={(_e, value) => handleValueChange(value)}
      onFocus={e => {
        e.stopPropagation();
        setTruncateTags(false);
      }}
      onInputChange={onInputChange}
    />
  );
}
